import {createPRForProject, Directory, FileTree, getProjectGithubFiles, scanProjectTSXFiles} from 'Common/api';
import {ProjectFileTree} from 'Components/ProjectFileTree';
import {Typography} from 'Components/Typography';
import {Spinner} from 'Components/Tailwind/Spinner';
import {useUserContext} from 'Contexts/UserContext/UserContext';
import React, {useCallback, useState} from 'react';
import {Redirect} from 'react-router-dom';
import type {Project as IProject} from 'rendition-core';
import {twMerge} from 'tailwind-merge';
import {useTimeoutEffect} from '@react-hookz/web';

interface Props {
    project: IProject;
}

const getFilesByFileTypeFromTree = (tree: FileTree, fileType: string) => {
    const files: {path: string; content: string}[] = [];
    const recurseTree = (tree: FileTree | Directory) => {
        tree.children.forEach((child) => {
            if (child.type === 'file') {
                if (child.name.endsWith(fileType) && child.path.includes('apps/web/src/app')) {
                    files.push({path: child.path, content: atob(child.contents)});
                }
            } else {
                recurseTree(child);
            }
        });
    };
    recurseTree(tree);
    return files;
};

export const Project: React.FC<Props> = ({project}) => {
    const {userId} = useUserContext();
    const [files, setFiles] = useState<FileTree>();
    const [prLink, setPRLink] = useState<string | null>(null);
    const [prLoading, setPRLoading] = useState(false);
    const [scanned, setScanned] = useState(false);
    const [scanError, setScanError] = useState<string | null>(null);
    const [scanLoading, setScanLoading] = useState(false);
    const [filesLoading, setFilesLoading] = useState(false);

    useTimeoutEffect(() => {
        if (scanned) setScanned(false);
    }, 5000);

    const fetchFiles = useCallback(async () => {
        setFilesLoading(true);
        try {
            const contents = await getProjectGithubFiles({userId, projectId: project.id});
            setFiles(contents);
            return contents;
        } finally {
            setFilesLoading(false);
        }
    }, [project.id, userId]);

    const scanTSXFiles = useCallback(async () => {
        try {
            setScanLoading(true);
            setScanError('');
            let fileTree = files;
            if (!files) {
                fileTree = await fetchFiles();
            }
            const filesToScan = getFilesByFileTypeFromTree(fileTree!, 'tsx');
            const result = await scanProjectTSXFiles({
                userId,
                repoId: project.github?.id!,
                files: filesToScan,
            });
            if (result.success) {
                setScanned(true);
            } else {
                setScanError('Something went wrong scanning files');
            }
        } catch (e) {
            console.log(e);
            setScanError('Something went wrong scanning files');
        } finally {
            setScanLoading(false);
        }
    }, [fetchFiles, files, project.github?.id, userId]);

    const doCreatePR = useCallback(async () => {
        try {
            setPRLoading(true);
            const {pr} = await createPRForProject({userId, projectId: project.id});
            setPRLink(pr.html_url);
        } finally {
            setPRLoading(false);
        }
    }, [project.id, userId]);

    const goToPR = useCallback(() => {
        window.open(prLink!, '_blank');
    }, [prLink]);

    if (!project.github) {
        return <Redirect to="/manage" />;
    }

    return (
        <div className="flex h-full w-full flex-col items-start gap-4 p-6">
            <h1 className="text-2xl font-black">{project.github.name}</h1>
            {!scanError && !scanned && (
                <Typography.P className="text-sky-500">
                    Note: Scanning files will be slow if you have a lot of files.
                </Typography.P>
            )}
            {scanError && (
                <div className="mb-2 flex h-20 w-full flex-row items-center justify-center rounded-lg border border-red-500 bg-red-500/30 p-4">
                    <Typography.P className="text-red-500">Something went scanning files</Typography.P>
                </div>
            )}
            <div className="flex flex-row items-center justify-start gap-4">
                <button onClick={fetchFiles} className="h-10 w-32 rounded-xl border border-black px-2 py-1 text-black">
                    {filesLoading ? <Spinner /> : 'Get Files'}
                </button>
                <button
                    onClick={scanTSXFiles}
                    disabled={scanned}
                    className={twMerge(
                        'flex h-10 w-48 items-center justify-center rounded-xl border border-black px-4 py-1 text-black',
                        scanned && 'border-green-500 bg-green-500/30 text-green-500'
                    )}
                >
                    {scanLoading ? <Spinner /> : 'Scan TSX Files'}
                </button>
                <button
                    onClick={prLink ? goToPR : doCreatePR}
                    className="flex h-10 w-32 items-center justify-center rounded-xl border border-black px-2 py-1 text-black"
                >
                    {prLoading ? <Spinner /> : prLink ? 'View PR' : 'Create PR'}
                </button>
            </div>
            {files && <ProjectFileTree tree={files} project={project} />}
        </div>
    );
};
